import { get } from "lodash";
import env from "@beam-australia/react-env";
import { perksEnabled } from "utils/perksEnabled";
import type { RootState } from "types/state";

export const getPath = () => {
  const domain = env("AUTH0_DOMAIN");
  const clientId = env("AUTH0_CLIENTID");
  const domainPath = `https://${env("AUTH0_DOMAIN")}`;
  if (domain === "login-pre-production.iconsavingsplan.com") {
    return `${domainPath}/${clientId}`;
  }
  return domainPath;
};

// auth0User is the Auth0 ID token payload stored on the user slice; the
// custom claims below are namespaced by getPath(). The paths are dynamic,
// so lodash get can't resolve types — each selector casts to the claim's
// known shape instead.

export const intercomHashSelector = (state: RootState) => {
  const intercomHashPath = `${getPath()}/intercom_hash`;
  const auth0User = get(state, `user.auth0User`);
  return auth0User[intercomHashPath] as string | undefined;
};

export const changeEmailSelector = (state: RootState) => {
  const changeEmailPath = `${getPath()}/change_email`;
  const auth0User = get(state, `user.auth0User`);
  return (auth0User[changeEmailPath] as string | null | undefined) || "";
};

export const emailConfirmedSelector = (state: RootState) => {
  const emailConfirmedPath = `${getPath()}/email_confirmed`;
  const auth0User = get(state, `user.auth0User`);
  return auth0User[emailConfirmedPath] as boolean | undefined;
};

export const iconIdSelector = (auth0User?: Record<string, unknown> | null) => {
  const iconUserIdPath = `${getPath()}/icon_id`;
  return get(auth0User, `${iconUserIdPath}`) as string | undefined;
};

export const auth0PlanCodeSelector = (state: RootState) => {
  const planCodePath = `${getPath()}/plan_code`;
  const auth0User = get(state, `user.auth0User`);

  return get(auth0User, planCodePath) as string | undefined;
};

export const auth0ReferralCodeSelector = (state: RootState) => {
  const referralCodePath = `${getPath()}/referral_code`;
  const auth0User = get(state, `user.auth0User`);

  return get(auth0User, referralCodePath) as string | undefined;
};

export const auth0AccountSessionSelector = (state: RootState) => {
  const activeAccountIdPath = `${getPath()}/active_account_id`;
  const auth0User = get(state, `user.auth0User`);

  return get(auth0User, activeAccountIdPath) as string | undefined;
};

export const hasPreTaxFundsSelector = (state: RootState) => {
  const hasPreTaxFundsPath = `${getPath()}/has_pretax_funds`;
  const auth0User = get(state, `user.auth0User`);

  return get(auth0User, hasPreTaxFundsPath) as boolean | undefined;
};

export const hasRothFundsSelector = (state: RootState) => {
  const hasRothFundsPath = `${getPath()}/has_roth_funds`;
  const auth0User = get(state, `user.auth0User`);

  return get(auth0User, hasRothFundsPath) as boolean | undefined;
};

export const hasBothPreTaxAndRothFundsSelector = (state: RootState) => {
  const hasPreTaxFunds = hasPreTaxFundsSelector(state);
  const hasRothFunds = hasRothFundsSelector(state);

  return hasPreTaxFunds && hasRothFunds;
};

export const paychexPortalSelector = (state: RootState) => {
  const paychexPortalPath = `${getPath()}/paychex_portal_url`;
  const auth0User = get(state, `user.auth0User`);

  return get(auth0User, paychexPortalPath) as string | undefined;
};

// Paychex Flex (Perks) entry flag — a namespaced custom claim on the Auth0 ID
// token (set from the user's appMetadata by an Auth0 Action). Distinct from the
// PAYCHEX_OAUTH 401k roll-in.
export const paychexPerksSelector = (state: RootState) => {
  if (!perksEnabled()) {
    return false;
  }
  const paychexPerksPath = `${getPath()}/is_paychex_perks_user`;
  const auth0User = get(state, `user.auth0User`);
  return Boolean(get(auth0User, paychexPerksPath));
};

// app_metadata key `needsPaychexPerksLink`. Takes the auth0User directly, like
// iconIdSelector — App reads it before the user slice is hydrated.
export const needsPaychexPerksLinkSelector = (
  auth0User?: Record<string, unknown> | null
) => {
  if (!perksEnabled()) {
    return false;
  }
  const needsPaychexPerksLinkPath = `${getPath()}/needs_paychex_perks_link`;
  return Boolean(get(auth0User, needsPaychexPerksLinkPath));
};

// No app_metadata flag behind this one, unlike the Perks claim: the post-login
// Action mints it from the arrival itself — a connection=Paychex login with no
// acr_values, which the Perks tile always sends and the rollover tile never does.
export const needsPaychexRolloverLinkSelector = (
  auth0User?: Record<string, unknown> | null
) => {
  const needsPaychexRolloverLinkPath = `${getPath()}/needs_paychex_rollover_link`;
  return Boolean(get(auth0User, needsPaychexRolloverLinkPath));
};
